import React, { useState, useRef, useEffect, useId } from 'react'
import { FiChevronDown } from 'react-icons/fi'

export interface SelectOption {
    value: string
    label: string
}

interface SelectDropdownProps {
    value: string
    onChange: (value: string) => void
    options: readonly SelectOption[]
    placeholder?: string
    label?: string
    className?: string
    disabled?: boolean
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
    value,
    onChange,
    options,
    placeholder = 'Select an option',
    label,
    className = '',
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom')
    const baseId = useId()
    const labelId = `${baseId}-label`
    const buttonId = `${baseId}-button`
    const listboxId = `${baseId}-listbox`
    const dropdownRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    // Get selected option label
    const selectedOption = options.find(opt => opt.value === value)
    const displayLabel = selectedOption?.label || placeholder

    // Calculate dropdown position (top or bottom)
    useEffect(() => {
        if (!isOpen || !buttonRef.current) return

        const calculatePosition = () => {
            const buttonRect = buttonRef.current!.getBoundingClientRect()
            const viewportHeight = window.innerHeight
            const dropdownHeight = 280 // Approximate height (max-h-64 + padding)
            const spaceBelow = viewportHeight - buttonRect.bottom
            const spaceAbove = buttonRect.top

            // If less than 300px space below and more space above, position on top
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                setDropdownPosition('top')
            } else {
                setDropdownPosition('bottom')
            }
        }

        calculatePosition()
        window.addEventListener('resize', calculatePosition)
        return () => window.removeEventListener('resize', calculatePosition)
    }, [isOpen])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setHighlightedIndex(-1)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return

        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault()
                setIsOpen(true)
                setHighlightedIndex(0)
            }
            return
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setHighlightedIndex(prev => 
                    prev < options.length - 1 ? prev + 1 : options.length - 1
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setHighlightedIndex(prev => 
                    prev > 0 ? prev - 1 : 0
                )
                break
            case 'Enter':
                e.preventDefault()
                if (highlightedIndex >= 0 && highlightedIndex < options.length) {
                    onChange(options[highlightedIndex].value)
                    setIsOpen(false)
                    setHighlightedIndex(-1)
                }
                break
            case 'Escape':
                e.preventDefault()
                setIsOpen(false)
                setHighlightedIndex(-1)
                buttonRef.current?.blur()
                break
            case 'Tab':
                setIsOpen(false)
                setHighlightedIndex(-1)
                break
        }
    }

    const handleOptionClick = (optionValue: string) => {
        onChange(optionValue)
        setIsOpen(false)
        setHighlightedIndex(-1)
    }

    return (
        <div ref={dropdownRef} className={`relative ${className}`}>
            {label && (
                <label id={labelId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {label}
                </label>
            )}
            
            {/* Trigger Button */}
            {isOpen ? (
                <button
                    id={buttonId}
                    ref={buttonRef}
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    aria-expanded="true"
                    aria-haspopup="listbox"
                    aria-controls={listboxId}
                    aria-labelledby={label ? `${labelId} ${buttonId}` : undefined}
                    aria-label={label ? undefined : displayLabel}
                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent text-xs sm:text-sm md:text-base font-medium text-gray-700 dark:text-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2"
                >
                    <span className="truncate">{displayLabel}</span>
                    <FiChevronDown 
                        className="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform flex-shrink-0 transform rotate-180"
                    />
                </button>
            ) : (
                <button
                    id={buttonId}
                    ref={buttonRef}
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    aria-expanded="false"
                    aria-haspopup="listbox"
                    aria-labelledby={label ? `${labelId} ${buttonId}` : undefined}
                    aria-label={label ? undefined : displayLabel}
                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent text-xs sm:text-sm md:text-base font-medium text-gray-700 dark:text-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2"
                >
                    <span className="truncate">{displayLabel}</span>
                    <FiChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform flex-shrink-0" />
                </button>
            )}

            {/* Dropdown Menu */}
            {isOpen && (
                <div 
                    ref={menuRef}
                    id={listboxId}
                    role="listbox"
                    aria-labelledby={label ? labelId : undefined}
                    className={`absolute left-0 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 overflow-hidden transition-all ${
                        dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                    }`}
                >
                    <div className="py-1 max-h-64 overflow-y-auto">
                        {options.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                                No options available
                            </div>
                        ) : (
                            options.map((option, index) => (
                                value === option.value ? (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => handleOptionClick(option.value)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        role="option"
                                        aria-selected="true"
                                        className="w-full px-4 py-2.5 text-left text-xs sm:text-sm md:text-base font-medium transition-colors flex items-center bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                                    >
                                        <span className="flex-1">{option.label}</span>
                                        <span className="text-primary-600 dark:text-primary-400 font-bold">✓</span>
                                    </button>
                                ) : (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => handleOptionClick(option.value)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        role="option"
                                        aria-selected="false"
                                        className={`w-full px-4 py-2.5 text-left text-xs sm:text-sm md:text-base font-medium transition-colors flex items-center ${
                                            index === highlightedIndex
                                                ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        <span className="flex-1">{option.label}</span>
                                    </button>
                                )
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default SelectDropdown